// Ghost-manager synthesis shared by both org views (force-directed Org Graph
// and top-down Org Tree).
//
// Some people have a `manager_name` that points OUTSIDE the scoped Entra
// export (e.g. 8 people report to "Linda Koivunen", who isn't in the export).
// The backend can't resolve those to a UPN, so it leaves `managerUpn` null and
// carries the raw name on `unresolvedManagerName`. Left alone, those people
// float as disconnected roots and the outside-export reporting line is
// invisible.
//
// `augmentWithGhosts` synthesises one greyed-out "ghost" manager node per
// unique unresolved name, re-parents the affected people under it, and emits a
// (dashed) ghost→person edge for each. Everyone sharing an unresolved manager
// hangs under one ghost, re-unifying that sub-org. The Org Tree consumes only
// the augmented node list (its layout re-derives edges from `managerUpn`); the
// Org Graph consumes both nodes and edges. See PRODOPSAI-475 / PRODOPSAI-484.

import type { IOrgGraphNode, IOrgGraphEdge } from '@skystone/shared';

/** Prefix for synthesised ghost-manager node ids. The id is
 *  `GHOST_ID_PREFIX + <manager name>`, so everyone sharing an unresolved
 *  manager hangs under one ghost. */
export const GHOST_ID_PREFIX = '__ghost__:';

/** True when `id` is a synthesised ghost-manager node. */
export function isGhostId(id: string): boolean {
  return id.startsWith(GHOST_ID_PREFIX);
}

/** Dashed stroke pattern (logical units on / off) for ghost reporting lines —
 *  the shared "leaves the P&E org" visual token. Used by the dashed `EdgeLine`
 *  and the `GraphGhostNode` border so the two stay in sync. */
export const GHOST_DASH = '5 4';

/** Build a ghost-manager node carrying just enough shape to satisfy
 *  `IOrgGraphNode`. Ghosts are roots (`managerUpn` null) and never themselves
 *  have an unresolved manager. */
function ghostNode(id: string, name: string): IOrgGraphNode {
  return {
    id,
    displayName: name,
    jobTitle: null,
    departmentId: 'unassigned',
    mail: null,
    managerUpn: null,
    unresolvedManagerName: null,
    isCurrentUser: false,
    isExternal: false,
  };
}

/** Result of {@link augmentWithGhosts}: the original roster plus synthesised
 *  ghost nodes, and the original edges plus ghost→person edges. */
export interface GhostAugmentedGraph {
  nodes: IOrgGraphNode[];
  edges: IOrgGraphEdge[];
}

/**
 * Synthesise ghost-manager bridge nodes for every unique `unresolvedManagerName`
 * in `nodes`, re-parent the affected people under their ghost, and append a
 * ghost→person edge for each. Pure — no React, no mutation of the inputs.
 *
 * @param nodes - the (already-filtered) roster.
 * @param edges - existing manager→report edges; defaults to none for callers
 *   that only need the augmented node list (the Org Tree).
 */
export function augmentWithGhosts(
  nodes: readonly IOrgGraphNode[],
  edges: readonly IOrgGraphEdge[] = [],
): GhostAugmentedGraph {
  const ghostIdByName = new Map<string, string>();
  const ghosts: IOrgGraphNode[] = [];
  const ghostEdges: IOrgGraphEdge[] = [];

  const reparented = nodes.map((n) => {
    const name = n.unresolvedManagerName?.trim() ?? '';
    if (name.length === 0) return n;
    let ghostId = ghostIdByName.get(name);
    if (ghostId === undefined) {
      ghostId = GHOST_ID_PREFIX + name;
      ghostIdByName.set(name, ghostId);
      ghosts.push(ghostNode(ghostId, name));
    }
    ghostEdges.push({ source: ghostId, target: n.id });
    return { ...n, managerUpn: ghostId };
  });

  return {
    nodes: [...ghosts, ...reparented],
    edges: [...edges, ...ghostEdges],
  };
}
